import axios from "axios";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import theme from "../styles/theme";
import CompetencyModal from "./CompetencyModal";
import { FlexGrow } from "./FlexBox";
import { ClickableIcon } from "./Icon";
import { UserContext } from "../context/UserContext";
import { Icon, Rating, Stack } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const Header = styled.h4`
  font-family: Montserrat;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;

const FlexBox = styled("div")`
  display: flex;
  margin-bottom: 5px;
  align-items: center;
`;
const CustomFlex = styled(FlexBox)`
  padding: 0px 0px 20px 0px;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;

const Card = styled("div")`
  background: #1e2131;
  border-radius: 5px;
  width: fit-content;
  padding: 20px;
  margin: 10px 0;
`;
type InstructorEvaluationModalProps = {
  isOpen?: boolean;
  data: any;
  semesterId: string;
  semesterName?: string;
  setOpen: (x: boolean) => void;
};

const InstructorEvaluationModal = ({
  isOpen,
  setOpen,
  data,
  semesterId,
  semesterName,
}: InstructorEvaluationModalProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [evaluationData, setEvaluationData] = useState<any>({});
  useEffect(() => {
    fetchEvaluation(data.competency_code, semesterId);
  }, [data, semesterId]);
  const currentUser = useContext(UserContext).user;

  const fetchEvaluation = (code: string, semester: string) => {
    setLoading(true);
    axios
      .get(
        `/instructor/evaluation?instructor_id=${currentUser.id}&competency_code=${data?.competency_code}&semester_id=${semesterId}`
      )
      .then((res) => {
        if (res.data) {
          setEvaluationData(res.data);
        } else {
          setEvaluationData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        setEvaluationData([]);
        setLoading(false);
      });
  };

  return (
    <>
      <CompetencyModal
        customStyle={{
          overlay: {
            zIndex: "1005",
          },
          content: {
            inset: `0px 0px 0px ${theme.sidebarWidth}`,
            maxWidth: "100%",
            background: "#0B0C1F",
            overflowY: "auto",
          },
        }}
        isOpen={isOpen || false}
        setOpen={setOpen}
      >
        <CustomFlex>
          <ClickableIcon
            size={3.125}
            fixedWidth={false}
            name="times"
            onClick={() => setOpen(false)}
          />
          <FlexGrow>
            <Header>Instructor Evaluation for {data?.competency_code}</Header>
          </FlexGrow>
        </CustomFlex>

        <div style={{ margin: "20px 0" }}>
          <FlexBox>
            <div style={{ minWidth: 300 }}>Competency Title</div>
            <div>: {data.title}</div>
          </FlexBox>
          <FlexBox>
            <div style={{ minWidth: 300 }}>Instructor Name</div>
            <div>: {currentUser.title + " " + currentUser.name}</div>
          </FlexBox>

          <FlexBox>
            <div style={{ minWidth: 300 }}>Semester</div>
            <div>: {semesterName}</div>
          </FlexBox>
          <FlexBox>
            <div style={{ minWidth: 300 }}>Number of Students responding</div>
            <div>: {evaluationData.student_count}</div>
          </FlexBox>
        </div>

        <div style={{ margin: "20px 0" }}>
          <span style={{ fontWeight: "bold" }}>Ratings</span>
          <Card>
            <FlexBox>
              <div style={{ minWidth: 200 }}>Overall Rating</div>
              {!evaluationData?.average_scores?.overall_rating ||
              evaluationData?.average_scores?.overall_rating === 0 ? (
                <div style={{ color: "yellow" }}>Not rated yet!</div>
              ) : (
                <>
                  <Rating
                    // name="text-feedback"
                    value={evaluationData?.average_scores?.overall_rating || 0}
                    readOnly
                    precision={0.5}
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: 24,
                        color: "#EAB828",
                      },
                      columnGap: "20px",
                    }}
                    emptyIcon={
                      <StarIcon
                        style={{ opacity: 1, color: "gray" }}
                        fontSize="inherit"
                      />
                    }
                  />
                  <div
                    style={{
                      color: "#00ff00",
                      minWidth: 100,
                      textAlign: "end",
                    }}
                  >
                    {evaluationData?.average_scores?.overall_rating || 0} / 5.00
                  </div>
                </>
              )}
            </FlexBox>
            <FlexBox>
              <div style={{ minWidth: 200 }}>Instructor Rating</div>
              {!evaluationData?.average_scores?.instructor_rating ||
              evaluationData?.average_scores?.instructor_rating === 0 ? (
                <div style={{ color: "yellow" }}>Not rated yet!</div>
              ) : (
                <>
                  <Rating
                    value={
                      evaluationData?.average_scores?.instructor_rating || 0
                    }
                    readOnly
                    precision={0.1}
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: 24,
                        color: "#EAB828",
                      },
                      columnGap: "20px",
                    }}
                    emptyIcon={
                      <StarIcon
                        style={{ opacity: 1, color: "gray" }}
                        fontSize="inherit"
                      />
                    }
                  />
                  <div
                    style={{
                      color: "#00ff00",
                      minWidth: 100,
                      textAlign: "end",
                    }}
                  >
                    {evaluationData?.average_scores?.instructor_rating || 0} /
                    5.00
                  </div>
                </>
              )}
            </FlexBox>
            <FlexBox>
              <div style={{ minWidth: 200 }}>Teaching Ability</div>
              {!evaluationData?.average_scores?.teaching_ability_rating ||
              evaluationData?.average_scores?.teaching_ability_rating === 0 ? (
                <div style={{ color: "yellow" }}>Not rated yet!</div>
              ) : (
                <>
                  <Rating
                    // name="text-feedback"
                    value={
                      evaluationData?.average_scores?.teaching_ability_rating ||
                      0
                    }
                    readOnly
                    precision={0.1}
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: 24,
                        color: "#EAB828",
                      },
                      columnGap: "20px",
                    }}
                    emptyIcon={
                      <StarIcon
                        style={{ opacity: 1, color: "gray" }}
                        fontSize="inherit"
                      />
                    }
                  />
                  <div
                    style={{
                      color: "#00ff00",
                      minWidth: 100,
                      textAlign: "end",
                    }}
                  >
                    {evaluationData?.average_scores?.teaching_ability_rating ||
                      0}{" "}
                    / 5.00
                  </div>
                </>
              )}
            </FlexBox>
            <FlexBox>
              <div style={{ minWidth: 200 }}>Assessment Rating</div>
              {!evaluationData?.average_scores?.assessment_rating ||
              evaluationData?.average_scores?.assessment_rating === 0 ? (
                <div style={{ color: "yellow" }}>Not rated yet!</div>
              ) : (
                <>
                  <Rating
                    // name="text-feedback"
                    value={
                      evaluationData?.average_scores?.assessment_rating || 0
                    }
                    readOnly
                    precision={0.1}
                    sx={{
                      "& .MuiRating-icon": {
                        fontSize: 24,
                        color: "#EAB828",
                      },
                      columnGap: "20px",
                    }}
                    emptyIcon={
                      <StarIcon
                        style={{ opacity: 1, color: "gray" }}
                        fontSize="inherit"
                      />
                    }
                  />
                  <div
                    style={{
                      color: "#00ff00",
                      minWidth: 100,
                      textAlign: "end",
                    }}
                  >
                    {evaluationData?.average_scores?.assessment_rating || 0} /
                    5.00
                  </div>
                </>
              )}
            </FlexBox>
          </Card>
        </div>
        {evaluationData.comments?.length > 0 && (
          <div>
            <span style={{ fontWeight: "bold" }}>Comments</span>
            <Card
              className="visible-scrollbar"
              style={{ maxHeight: "250px", overflowY: "auto", width: "auto" }}
            >
              {evaluationData.comments
                ?.filter((v: any) => typeof v === "string" && v.length > 2)
                .map((v: any, i: number) => (
                  <FlexBox
                    key={i}
                    style={{ alignItems: "start", marginBottom: "12px" }}
                  >
                    <Icon
                      sx={{
                        // color: "green",
                        padding: "0px 16px",
                      }}
                    >
                      sms
                    </Icon>
                    <span>"{v}"</span>
                  </FlexBox>
                ))}
            </Card>
          </div>
        )}
      </CompetencyModal>
    </>
  );
};

export default InstructorEvaluationModal;
